/**
 * InventoryMode — the project-wide view: every input/output merged across
 * processes, enriched with its impact-dataset linkage and cost so the page actually
 * shows what's in the project (not just a name + a number). Per-process editing
 * still lives in Build mode's NodeInspector; this is the read-oriented "what's in
 * the whole project" overview, plus Save / Overwrite Template.
 */

import React from 'react';
import { useWorkspace } from './WorkspaceContext';
import { fmt3 } from './workspaceFormat';
import { isBuiltinTemplateFile } from '../../services/templateCatalog';
import { getActiveImpactSource, impactSourceLabel } from '../../services/impactSource';

const fmtMoney = (n) =>
  typeof n === 'number' && n > 0
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(n)
    : null;

const parseInputs = (raw) =>
  Array.isArray(raw) ? raw
    : (typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch { return []; } })() : []);

export default function InventoryMode() {
  const {
    project, mergedInventory,
    currentTemplateFileName, handleOverwriteTemplate,
    showTemplateSaveDialog, setShowTemplateSaveDialog,
    newTemplateName, setNewTemplateName, handleSaveNewTemplate,
  } = useWorkspace();

  if (!project?.processes) {
    return <div className="no-selection"><p>No project loaded</p></div>;
  }

  const fromBuiltin = isBuiltinTemplateFile(currentTemplateFileName);

  const procEntries = Object.values(project.processes);
  const processCount = procEntries.length;
  const inputs = mergedInventory.inputs || [];
  const outputs = mergedInventory.outputs || [];
  const totalInputCost = inputs.reduce((sum, i) => sum + (i.totalCost || 0), 0);
  const linkedCount = inputs.filter((i) => i.hasScores).length;
  const datasetLabel = impactSourceLabel(getActiveImpactSource(project));
  const chemistry = project.project_info?.chemistry || project.project_info?.category || null;
  const projectName = project.project_info?.name || project.project_name || 'Project';

  return (
    <div className="workspace-scroll">
      <div className="inv-wrap">
        {/* Header + at-a-glance chips */}
        <div className="inv-header">
          <div className="inv-title">{projectName}</div>
          <div className="inv-meta">
            {chemistry && <span className="inv-chip">{chemistry}</span>}
            <span className="inv-chip">{processCount} step{processCount !== 1 ? 's' : ''}</span>
            <span className="inv-chip">{inputs.length} input{inputs.length !== 1 ? 's' : ''}</span>
            {linkedCount > 0 && <span className="inv-chip inv-chip-eco">{linkedCount} {datasetLabel}-linked</span>}
            {linkedCount < inputs.length && <span className="inv-chip">{inputs.length - linkedCount} not linked</span>}
            {totalInputCost > 0 && <span className="inv-chip inv-chip-cost">{fmtMoney(totalInputCost)} input cost</span>}
          </div>
          <div className="inv-subtle">Combined across all processes. To edit a step's inputs, open it in Build mode.</div>
        </div>

        {/* Inputs — the rich table */}
        <div className="inv-section">
          <div className="inv-section-label">Inputs</div>
          <div className="inv-table inv-table--inputs">
            <div className="inv-row inv-row-head">
              <span className="inv-c-name">Material</span>
              <span className="inv-c-link">{datasetLabel} process</span>
              <span className="inv-c-qty">Quantity</span>
              <span className="inv-c-cost">$/unit</span>
              <span className="inv-c-total">Total cost</span>
            </div>
            {inputs.length === 0 && <div className="inv-empty">No inputs defined.</div>}
            {inputs.map((item, idx) => (
              <div key={idx} className="inv-row">
                <span className="inv-c-name">
                  <span className="inv-name-main" title={item.name}>{item.name}</span>
                  {item.processCount > 0 && (
                    <span className="inv-name-sub" title={item.processNames.join(', ')}>
                      used in {item.processCount} process{item.processCount !== 1 ? 'es' : ''}
                    </span>
                  )}
                </span>
                <span className="inv-c-link">
                  {item.activity_name ? (
                    <>
                      <span className="inv-eco-dot" title={`Linked to a ${datasetLabel} activity`} />
                      <span className="inv-eco-name" title={item.activity_name}>{item.activity_name}</span>
                      {item.location && <span className="inv-eco-loc">{item.location}</span>}
                    </>
                  ) : (
                    <span className="inv-unlinked">{item.hasScores ? 'has impact data' : `no ${datasetLabel} link`}</span>
                  )}
                </span>
                <span className="inv-c-qty">{fmt3(item.quantity)}<span className="inv-unit">{item.unit}</span></span>
                <span className="inv-c-cost">{item.cost_per_unit != null ? `$${fmt3(item.cost_per_unit)}` : '—'}</span>
                <span className="inv-c-total">{fmtMoney(item.totalCost) || '—'}</span>
              </div>
            ))}
            {totalInputCost > 0 && (
              <div className="inv-row inv-row-total">
                <span className="inv-c-name">Total input cost</span>
                <span className="inv-c-link" />
                <span className="inv-c-qty" />
                <span className="inv-c-cost" />
                <span className="inv-c-total">{fmtMoney(totalInputCost)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Outputs */}
        {outputs.length > 0 && (
          <div className="inv-section">
            <div className="inv-section-label">Outputs</div>
            <div className="inv-table inv-table--outputs">
              <div className="inv-row inv-row-head">
                <span className="inv-c-name">Material</span>
                <span className="inv-c-qty">Quantity</span>
                <span className="inv-c-cost">Selling price</span>
              </div>
              {outputs.map((item, idx) => (
                <div key={idx} className="inv-row">
                  <span className="inv-c-name"><span className="inv-name-main">{item.name}</span></span>
                  <span className="inv-c-qty">{fmt3(item.quantity)}<span className="inv-unit">{item.unit}</span></span>
                  <span className="inv-c-cost">
                    {item.price_per_unit != null ? `$${fmt3(item.price_per_unit)}/${item.unit}` : '—'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Process parameters — compact, collapsed by default so it never overwhelms */}
        <details className="inv-params">
          <summary>Process steps &amp; parameters ({processCount})</summary>
          <div className="inv-table inv-table--params">
            <div className="inv-row inv-row-head">
              <span className="inv-c-name">Process</span>
              <span className="inv-c-link">Type</span>
              <span className="inv-c-params">Parameters</span>
              <span className="inv-c-qty">Inputs</span>
            </div>
            {procEntries.map((p, idx) => {
              const params = p.parameters && typeof p.parameters === 'object' ? p.parameters : {};
              const paramStr = Object.entries(params)
                .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
                .join('  ·  ');
              const nInputs = parseInputs(p.lci_inputs).length;
              return (
                <div key={idx} className="inv-row">
                  <span className="inv-c-name">
                    <span className="inv-name-main">{(p.name || p.id || 'Process').replace(/_/g, ' ')}</span>
                  </span>
                  <span className="inv-c-link inv-eco-name">
                    {(p.process_type || p.manufacturing_category || '—').replace(/_/g, ' ')}
                  </span>
                  <span className="inv-c-params">{paramStr || '—'}</span>
                  <span className="inv-c-qty">{nInputs}</span>
                </div>
              );
            })}
          </div>
        </details>

        {/* Save as Template. Built-in templates can't be overwritten: a saved
            copy under their file name would replace them for later projects. */}
        <div className="inv-template-bar">
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {!fromBuiltin && (
              <button
                onClick={handleOverwriteTemplate}
                style={{ padding: '6px 14px', fontSize: '12px', fontWeight: 500, background: '#8e44ad', color: '#fff', border: '1px solid #8e44ad', borderRadius: 4, cursor: 'pointer' }}
                title={`Overwrite ${currentTemplateFileName} — sub-inventory references will pick this up automatically`}
              >Overwrite Template</button>
            )}
            <button
              onClick={() => { setShowTemplateSaveDialog(!showTemplateSaveDialog); setNewTemplateName(''); }}
              style={{ padding: '6px 14px', fontSize: '12px', fontWeight: 500, background: '#fff', color: '#8e44ad', border: '1px solid #8e44ad', borderRadius: 4, cursor: 'pointer' }}
            >Save as New Template</button>
          </div>
          {showTemplateSaveDialog && (
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'center', marginTop: '8px' }}>
              <input
                type="text"
                value={newTemplateName}
                onChange={e => setNewTemplateName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSaveNewTemplate()}
                placeholder="Template name…"
                autoFocus
                style={{ padding: '4px 8px', fontSize: '12px', border: '1px solid #ccc', borderRadius: 4, width: '180px' }}
              />
              <button
                onClick={handleSaveNewTemplate}
                disabled={!newTemplateName.trim()}
                style={{ padding: '4px 10px', fontSize: '12px', fontWeight: 500, background: newTemplateName.trim() ? '#2e7d32' : '#ccc', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
              >Save</button>
            </div>
          )}
          <div style={{ fontSize: '11px', color: '#999', marginTop: '6px', textAlign: 'center' }}>
            {fromBuiltin
              ? <>Built from the built-in <strong>{currentTemplateFileName}</strong>, which can't be overwritten. Save your changes as a new template.</>
              : <>Overwrite updates <strong>{currentTemplateFileName}</strong> for sub-inventory use</>}
          </div>
        </div>
      </div>
    </div>
  );
}
